import { useState } from 'react';

import type { AdminPack, AdminPackInput } from '@/services/admin';

import { usePaymentsAdmin } from './usePaymentsAdmin';

const EMPTY: AdminPackInput = { id: '', label: '', credits: 10, sort: 100, active: false, priceStars: null };

function toInput(pack: AdminPack): AdminPackInput {
  return {
    id: pack.id,
    label: pack.label,
    credits: pack.credits,
    sort: pack.sort,
    active: pack.active,
    priceStars: pack.priceStars,
  };
}

/**
 * Каталог пакетов. Форма не знает пределов: границы и правило «нельзя
 * продавать без цены» проверяет сервер, а его отказ показывается как есть.
 */
export function PacksAdmin() {
  const { packs, savePack } = usePaymentsAdmin();
  const [draft, setDraft] = useState<AdminPackInput>(EMPTY);

  return (
    <section aria-label="Пакеты кредитов" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-text">Пакеты кредитов</h2>
      <p className="text-xs text-text-3">Один кредит — один кадр. Цена в Stars; неактивный пакет людям не показывается.</p>

      {packs.isPending && <p className="text-text-2">Загрузка…</p>}
      {packs.isError && <p className="text-danger">Не удалось загрузить пакеты. Обновите страницу.</p>}

      {savePack.isError && (
        <p role="alert" className="text-sm text-danger">
          {savePack.error instanceof Error ? savePack.error.message : 'Не удалось сохранить пакет.'}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {packs.data?.packs.map((pack) => (
          <li key={pack.id} className="flex items-center justify-between gap-2 text-sm text-text">
            <span>
              {pack.label} · {pack.credits} кр. · {pack.priceStars === null ? 'без цены' : `${pack.priceStars} ⭐`} ·{' '}
              {pack.active ? 'продаётся' : 'скрыт'}
            </span>
            <button type="button" onClick={() => setDraft(toInput(pack))} className="btn btn-secondary px-3 py-1">
              Изменить
            </button>
          </li>
        ))}
      </ul>

      <form
        className="flex flex-col gap-2 rounded-card border border-border p-3"
        onSubmit={(event) => {
          event.preventDefault();
          savePack.mutate(draft, { onSuccess: () => setDraft(EMPTY) });
        }}
      >
        <label className="flex flex-col gap-1 text-sm text-text">
          Код (латиница, цифры, дефис)
          <input value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value })} className="rounded-lg border border-border-strong bg-surface px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text">
          Подпись
          <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="rounded-lg border border-border-strong bg-surface px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text">
          Кредитов в пакете
          <input type="number" value={draft.credits} onChange={(e) => setDraft({ ...draft, credits: Number(e.target.value) })} className="rounded-lg border border-border-strong bg-surface px-3 py-2 tabular-nums" />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text">
          Цена в Stars (пусто — нет цены)
          <input
            type="number"
            value={draft.priceStars ?? ''}
            onChange={(e) => setDraft({ ...draft, priceStars: e.target.value === '' ? null : Number(e.target.value) })}
            className="rounded-lg border border-border-strong bg-surface px-3 py-2 tabular-nums"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-text">
          Порядок
          <input type="number" value={draft.sort} onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) })} className="rounded-lg border border-border-strong bg-surface px-3 py-2 tabular-nums" />
        </label>
        <label className="flex items-center gap-2 text-sm text-text">
          <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
          Продаётся
        </label>
        <button type="submit" disabled={savePack.isPending} className="btn btn-secondary self-start px-3 py-2 text-sm">
          Сохранить пакет
        </button>
      </form>
    </section>
  );
}
